import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	Output,
	SimpleChanges,
} from '@angular/core';
import {
	AbstractControl,
	FormBuilder,
	FormGroup,
	ValidationErrors,
	Validators,
} from '@angular/forms';
import { FreudMessage, FreudMessageService } from '@freud-ds/web-components';
import { TranslateService } from '@ngx-translate/core';
import { regions } from 'app/interfaces/location.interface';
import { LocationService } from 'app/services/location.service';

@Component({
	selector: 'address-form',
	templateUrl: './address-form.component.html',
	styleUrls: ['./address-form.component.scss'],
})
export class AddressFormComponent implements OnChanges {
	@Input() action: 'create' | 'edit' = 'create';
	@Input() initialValue;
	@Output() onFormInit = new EventEmitter();
	public addressForm: FormGroup = null;
	public regions = regions;
	public isFormLoaded = false;
	public addressByPostalCode;
	constructor(
		public locationService: LocationService,
		public messageService: FreudMessageService,
		public fb: FormBuilder,
		public translateService: TranslateService
	) {}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.action || changes.initialValue) {
			this.setForms(changes.initialValue.currentValue);
		}
	}

	setForms(value) {
		this.addressForm = this.fb.group({
			name: this.fb.control(
				this.action === 'edit' ? value?.name : '',
				Validators.minLength(3)
			),
			postalCode: this.fb.control(
				this.action === 'edit' ? value?.postalCode : '',
				this.validateControl(this.validatePostalCode)
			),
			street: this.fb.control(
				this.action === 'edit' ? value?.street : '',
				Validators.required
			),
			number: this.fb.control(
				this.action === 'edit' ? value?.number : '',
				Validators.required
			),
			neighborhood: this.fb.control(
				this.action === 'edit' ? value?.neighborhood : '',
				Validators.required
			),
			city: this.fb.control(
				this.action === 'edit' ? value?.city : '',
				Validators.required
			),
			state: this.fb.control(
				this.action === 'edit' ? value?.state : '',
				Validators.required
			),
			country: this.fb.control(
				this.action === 'edit' ? value?.country : '',
				Validators.required
			),
			complement: this.fb.control(
				this.action === 'edit' ? value?.complement : ''
			),
			phone: this.fb.control(
				this.action === 'edit' ? value?.phone : '',
				this.validateControl(this.validatePhone)
			),
		});
		this.onFormInit.emit(this.addressForm);
		this.isFormLoaded = true;
	}

	private validateControl(func: CallableFunction) {
		return (control: AbstractControl): ValidationErrors | null => {
			if (!func.bind(this)(control.value)) {
				return { invalid: true };
			}
		};
	}

	private validatePostalCode(postalCode: string) {
		return (postalCode || '').replace(/\D/g, '').length === 8;
	}

	private validatePhone(phone: string) {
		if (typeof phone !== 'string') return false;
		phone = phone.replace(/\D/g, '');

		if (!(phone.length >= 10 && phone.length <= 11)) {
			return false;
		}

		if (phone.length == 11 && parseInt(phone.substring(2, 3)) != 9) {
			return false;
		}

		for (let n = 0; n < 10; n++) {
			if (
				phone == new Array(11).join(n.toString()) ||
				phone == new Array(12).join(n.toString())
			) {
				return false;
			}
		}

		const DDDcodes = [
			11, 12, 13, 14, 15, 16, 17, 18, 19, 21, 22, 24, 27, 28, 31, 32, 33, 34,
			35, 37, 38, 41, 42, 43, 44, 45, 46, 47, 48, 49, 51, 53, 54, 55, 61, 62,
			64, 63, 65, 66, 67, 68, 69, 71, 73, 74, 75, 77, 79, 81, 82, 83, 84, 85,
			86, 87, 88, 89, 91, 92, 93, 94, 95, 96, 97, 98, 99,
		];
		if (DDDcodes.indexOf(parseInt(phone.substring(0, 2))) == -1) {
			return false;
		}

		if (
			phone.length == 10 &&
			[2, 3, 4, 5, 7].indexOf(parseInt(phone.substring(2, 3))) == -1
		)
			return false;

		return true;
	}

	toast(type: 'success' | 'error', message) {
		const toast: FreudMessage = {
			severity: type,
			summary: message,
		};
		this.messageService.add(toast);
	}

	onPostalCodeChange() {
		const postalCode = this.addressForm.controls.postalCode;

		if (postalCode.valid) {
			this.locationService
				.getAddressByPostalCode(postalCode.value)
				.toPromise()
				.then((res) => {
					if (res.erro) {
						this.addressForm.controls.postalCode.setErrors({
							incorrect: true,
						});
						this.toast(
							'error',
							this.translateService.instant(`PAGES.ADDRESS.INVALID_CEP`)
						);
						return;
					}
					this.setAddress(res);
				});
		}
	}

	setAddress(address) {
		this.addressByPostalCode = {
			street: address.logradouro,
			neighborhood: address.bairro,
			city: address.localidade,
			complement: address.complemento,
			state: address.uf,
			country: 'Brasil',
		};
		this.addressForm.patchValue(this.addressByPostalCode);
		for (const key in this.addressForm.controls) {
			if (
				Object.prototype.hasOwnProperty.call(this.addressForm.controls, key)
			) {
				this.addressForm.controls[key].markAsDirty();
			}
		}
	}
}
